import { Select, SortIcon } from "@au-schein-de/au-ui-library";

export type SortDropdownProps<TValue extends string> = {
	value: TValue;
	onValueChange: (value: TValue) => void;
	options: readonly TValue[];
	getOptionLabel: (option: TValue) => string;
	triggerTestId: string;
	getOptionTestId?: (option: TValue) => string;
	triggerAriaLabel?: string;
	triggerClassName?: string;
	contentAlign?: "start" | "center" | "end";
};

/**
 * Shared sort select used on product list and profile reviews hub.
 * Styling comes from Select variant="dropdown" in the UI library.
 */
export function SortDropdown<TValue extends string>({
	value,
	onValueChange,
	options,
	getOptionLabel,
	triggerTestId,
	getOptionTestId = (option) => option,
	triggerAriaLabel,
	triggerClassName = "min-w-max",
	contentAlign = "end",
}: SortDropdownProps<TValue>) {
	return (
		<Select.Root variant="dropdown" value={value} onValueChange={onValueChange}>
			<Select.Trigger
				autoFocus={false}
				className={triggerClassName}
				data-testid={triggerTestId}
				aria-label={triggerAriaLabel}
			>
				<Select.Value
					className="flex gap-2"
					placeholder={
						<span className="flex gap-2">
							<SortIcon size={6} />
							{getOptionLabel(value)}
						</span>
					}
				/>
			</Select.Trigger>
			<Select.Content
				className="[&_span]:has-[>svg]:hidden"
				align={contentAlign}
			>
				<Select.Group>
					{options.map((option) => (
						<Select.Item
							key={option}
							value={option}
							className="hover:text-gray-700 focus:text-gray-700 data-[highlighted]:text-gray-700"
							data-testid={getOptionTestId(option)}
						>
							<span className="flex items-center gap-2">
								<SortIcon size={6} />
								{getOptionLabel(option)}
							</span>
						</Select.Item>
					))}
				</Select.Group>
			</Select.Content>
		</Select.Root>
	);
}
